import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import { authGet, unwrapRows } from "../../lib/authApi";

const WAITING_STATUSES = new Set(["ordered", "collected", "received", "in_process", "result_imported", "review"]);
const FINAL_STATUSES = new Set(["final"]);

function normalStatus(value) {
  return String(value || "ordered").trim().toLowerCase();
}

function titleStatus(value) {
  return normalStatus(value).replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusColor(value) {
  const status = normalStatus(value);
  if (status === "final") return "success";
  if (["received", "in_process", "result_imported", "review"].includes(status)) return "info";
  if (status === "collected") return "warning";
  if (status === "cancelled") return "error";
  return "default";
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadOrganizations() {
      setLoading(true);
      setError("");
      try {
        const rows = unwrapRows(await authGet("/organizations"));
        if (cancelled) return;
        setOrgs(rows);
        const saved = localStorage.getItem("org_id");
        setOrgId(saved && rows.some((row) => row.id === saved) ? saved : rows[0]?.id || "");
        if (!rows.length) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setOrgs([]);
          setOrders([]);
          setPatients([]);
          setError(err.message || "Unable to load organizations for the dashboard.");
          setLoading(false);
        }
      }
    }
    loadOrganizations();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadDashboard() {
      if (!orgId) return;
      setLoading(true);
      setError("");
      try {
        const encodedOrg = encodeURIComponent(orgId);
        const [orderResponse, patientResponse] = await Promise.all([
          authGet(`/test-orders?org_id=${encodedOrg}&limit=500`),
          authGet(`/patients?org_id=${encodedOrg}&limit=5000`),
        ]);
        if (cancelled) return;
        setOrders(unwrapRows(orderResponse));
        setPatients(unwrapRows(patientResponse));
      } catch (err) {
        if (!cancelled) {
          setOrders([]);
          setPatients([]);
          setError(err.message || "Unable to load dashboard data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadDashboard();
    return () => { cancelled = true; };
  }, [orgId]);

  const metrics = useMemo(() => {
    const waiting = orders.filter((order) => WAITING_STATUSES.has(normalStatus(order.status))).length;
    const inProcess = orders.filter((order) => ["received", "in_process", "result_imported", "review"].includes(normalStatus(order.status))).length;
    const final = orders.filter((order) => FINAL_STATUSES.has(normalStatus(order.status))).length;
    return { waiting, inProcess, final, total: orders.length, patients: patients.length };
  }, [orders, patients]);

  const statusData = useMemo(() => {
    const counts = new Map();
    orders.forEach((order) => {
      const label = titleStatus(order.status);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts, ([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
  }, [orders]);

  const recentRows = useMemo(() => orders.slice(0, 12).map((order) => ({
    id: order.id,
    accession_number: order.accession_number || "—",
    patient_name: order.patient_name || "—",
    provider_name: order.provider_name || "—",
    test_type: order.test_type || "—",
    status: normalStatus(order.status),
    created_at: formatDate(order.created_at),
  })), [orders]);

  const columns = [
    { field: "accession_number", headerName: "Accession", width: 180 },
    { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 180 },
    { field: "provider_name", headerName: "Provider", flex: 1, minWidth: 180 },
    { field: "test_type", headerName: "Test", width: 100 },
    { field: "status", headerName: "Status", width: 145, renderCell: ({ value }) => <Chip size="small" label={titleStatus(value)} color={statusColor(value)} /> },
    { field: "created_at", headerName: "Created", width: 190 },
  ];

  const metricCards = [
    ["Total Orders", metrics.total, "All staged orders for this organization", "primary.main"],
    ["Waiting List", metrics.waiting, "Orders awaiting completion or results", "warning.main"],
    ["In Process", metrics.inProcess, "Received, processing, or under review", "info.main"],
    ["Patients", metrics.patients, "Active patient records in this organization", "success.main"],
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={2}>
        <Header title="Operations Dashboard" subtitle="A live view of your test-order workflow" />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {orgs.length > 1 && (
            <TextField select size="small" label="Organization" value={orgId} onChange={(event) => { setOrgId(event.target.value); localStorage.setItem("org_id", event.target.value); }} sx={{ minWidth: 220 }}>
              {orgs.map((org) => <MenuItem key={org.id} value={org.id}>{org.name || org.id}</MenuItem>)}
            </TextField>
          )}
          <Button variant="outlined" onClick={() => navigate("/intake")}>View Waiting List</Button>
          <Button variant="contained" onClick={() => navigate("/new-test-order")}>New Test Order</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} mb={2}>
        {metricCards.map(([label, value, helper, color]) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">{label}</Typography>
                <Typography variant="h3" color={color}>{loading ? "—" : value}</Typography>
                <Typography variant="caption" color="text.secondary">{helper}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!loading && !error && !orgs.length && (
        <Alert severity="info">No organization is assigned to this account yet. Ask an Owner to assign organization access.</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: 340 }}>
            <CardContent sx={{ height: "100%" }}>
              <Typography variant="h6" gutterBottom>Orders by Status</Typography>
              {loading ? <Typography color="text.secondary">Loading dashboard data…</Typography> : statusData.length ? (
                <ResponsiveContainer width="100%" height="86%">
                  <BarChart data={statusData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="status" width={110} />
                    <Tooltip />
                    <Bar dataKey="count" name="Orders" fill="#167a55" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography color="text.secondary">No test orders have been created for this organization yet.</Typography>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: 340 }}>
            <CardContent sx={{ height: "100%" }}>
              <Typography variant="h6" gutterBottom>Recent Test Orders</Typography>
              <Box sx={{ height: 260 }}>
                <DataGrid
                  rows={recentRows}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  getRowId={(row) => row.id}
                  localeText={{ noRowsLabel: error ? "Dashboard data could not be loaded." : "No test orders yet." }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
